import React, { createContext, useContext, useReducer, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import logger from '../utils/logger';

const initialState = {
  items: [],
  total: 0,
  itemCount: 0,
};

const ADD_TO_CART = 'ADD_TO_CART';
const REMOVE_FROM_CART = 'REMOVE_FROM_CART';
const UPDATE_QUANTITY = 'UPDATE_QUANTITY';
const CLEAR_CART = 'CLEAR_CART';
const LOAD_CART = 'LOAD_CART';

const recalc = (items) => ({
  items,
  itemCount: items.reduce((n, item) => n + (parseInt(item.quantity) || 0), 0),
  total: items.reduce((sum, item) => {
    const price = typeof item.price === 'number' && !isNaN(item.price) ? item.price : (parseFloat(item.price) || 0);
    return sum + price * (parseInt(item.quantity) || 0);
  }, 0),
});

const cartReducer = (state, action) => {
  switch (action.type) {
    case LOAD_CART:
      return recalc(action.payload);

    case ADD_TO_CART: {
      const { product, quantity = 1, options = {}, configuratorOptions = null, personalizationText = '' } = action.payload;
      logger.cartAction('ADD_TO_CART', { productName: product.name, options, personalizationText });

      const existingIdx = state.items.findIndex(item =>
        item.id === product.id &&
        JSON.stringify(item.options) === JSON.stringify(options) &&
        JSON.stringify(item.configuratorOptions) === JSON.stringify(configuratorOptions) &&
        item.personalizationText === personalizationText
      );

      let updatedItems;
      if (existingIdx >= 0) {
        updatedItems = state.items.map((item, i) =>
          i === existingIdx ? { ...item, quantity: item.quantity + quantity } : item
        );
      } else {
        const price = typeof product.price === 'number' && !isNaN(product.price)
          ? product.price : (parseFloat(product.price) || 0);
        updatedItems = [...state.items, {
          ...product,
          price,
          quantity,
          options,
          configuratorOptions,
          personalizationText,
          itemTotal: price * quantity,
        }];
      }
      return recalc(updatedItems);
    }

    case REMOVE_FROM_CART:
      return recalc(state.items.filter(item => item.cartItemId !== action.payload));

    case UPDATE_QUANTITY: {
      const { cartItemId, quantity } = action.payload;
      return recalc(state.items.map(item =>
        item.cartItemId === cartItemId
          ? { ...item, quantity, itemTotal: item.price * quantity }
          : item
      ));
    }

    case CLEAR_CART:
      return initialState;

    default:
      return state;
  }
};

const CartContext = createContext();
export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within a CartProvider');
  return ctx;
};

const generateCartItemId = () =>
  Date.now().toString(36) + Math.random().toString(36).substring(2);

const itemToRow = (item, userId) => ({
  user_id: userId,
  product_id: String(item.id),
  quantity: item.quantity,
  options: item.options || {},
  configurator_options: item.configuratorOptions ?? null,
  personalization_text: item.personalizationText || '',
  product_snapshot: {
    ...item,
    // exclude derived/transient fields from snapshot
    quantity: undefined,
    itemTotal: undefined,
    options: undefined,
    configuratorOptions: undefined,
    personalizationText: undefined,
  },
});

const rowToItem = (row) => {
  const snap = row.product_snapshot || {};
  return {
    ...snap,
    id: row.product_id,
    cartItemId: snap.cartItemId || generateCartItemId(),
    quantity: row.quantity,
    options: row.options || {},
    configuratorOptions: row.configurator_options ?? null,
    personalizationText: row.personalization_text || '',
    itemTotal: (snap.price || 0) * row.quantity,
  };
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState, () => {
    try {
      const localData = localStorage.getItem('kbrosCart');
      return localData ? JSON.parse(localData) : initialState;
    } catch {
      return initialState;
    }
  });

  // Track the current authenticated user without depending on AuthContext
  const userRef = useRef(null);
  // Keep a ref to current items for use in the debounced sync
  const itemsRef = useRef(state.items);
  useEffect(() => { itemsRef.current = state.items; }, [state.items]);

  // Persist to localStorage on every change
  useEffect(() => {
    try {
      localStorage.setItem('kbrosCart', JSON.stringify(state));
    } catch (err) {
      logger.error('Error saving cart to localStorage', { error: err.message });
    }
  }, [state]);

  // Full-replace sync: delete user's server cart then re-insert current items
  const syncToServer = useCallback(async (items, userId) => {
    if (!userId) return;
    try {
      await supabase.from('cart_items').delete().eq('user_id', userId);
      if (items.length > 0) {
        const { error } = await supabase.from('cart_items').insert(items.map(i => itemToRow(i, userId)));
        if (error) logger.error('Cart sync insert error', { error: error.message });
      }
    } catch (err) {
      logger.error('Cart sync exception', { error: err.message });
    }
  }, []);

  // Debounced sync — batches rapid cart changes into a single server write
  const syncTimer = useRef(null);
  const scheduleSyncToServer = useCallback(() => {
    if (!userRef.current) return;
    if (syncTimer.current) clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(() => {
      syncToServer(itemsRef.current, userRef.current);
    }, 1000);
  }, [syncToServer]);

  // Sync to server whenever cart changes (only when logged in)
  useEffect(() => {
    if (userRef.current) scheduleSyncToServer();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  // Auth state listener — loads server cart on login, cleans up on logout
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      const userId = session?.user?.id ?? null;
      userRef.current = userId;

      if (userId && event === 'INITIAL_SESSION') {
        // Could be a returning session (page refresh) OR a post-login redirect
        // (login redirects to /account causing a full page reload, so Supabase
        // fires INITIAL_SESSION instead of SIGNED_IN).
        //
        // Strategy: server cart is authoritative when it has items (returning user).
        // If server cart is empty but local has items, this is almost certainly a
        // fresh login redirect — save the guest cart to server. clearCart() wipes
        // both server and localStorage, so a legitimately-cleared server cart will
        // also have no local items, avoiding false restores.
        try {
          const { data: rows } = await supabase
            .from('cart_items')
            .select('*')
            .eq('user_id', userId);

          const serverItems = (rows || []).map(rowToItem);
          const localItems = itemsRef.current;

          if (serverItems.length > 0) {
            // Server has a cart — it's authoritative (returning user, no merge needed)
            dispatch({ type: LOAD_CART, payload: serverItems });
          } else if (localItems.length > 0) {
            // Server empty but local has items — fresh login redirect with guest cart
            dispatch({ type: LOAD_CART, payload: localItems });
            await syncToServer(localItems, userId);
          }
          // Both empty: nothing to do
        } catch (err) {
          logger.error('Error loading server cart on session restore', { error: err.message });
        }
      } else if (userId && event === 'SIGNED_IN') {
        // Guest just logged in. Merge any in-session guest cart with their stored server cart.
        try {
          const { data: rows } = await supabase
            .from('cart_items')
            .select('*')
            .eq('user_id', userId);

          const serverItems = (rows || []).map(rowToItem);
          const localItems = itemsRef.current;

          if (localItems.length === 0) {
            // Guest had no cart — just restore their server cart
            dispatch({ type: LOAD_CART, payload: serverItems });
          } else if (serverItems.length === 0) {
            // No stored server cart — save the guest cart as-is
            dispatch({ type: LOAD_CART, payload: localItems });
            await syncToServer(localItems, userId);
          } else {
            // Both exist — merge: server cart as base, fold in guest items
            const merged = [...serverItems];
            for (const local of localItems) {
              const serverMatch = serverItems.find(si =>
                String(si.id) === String(local.id) &&
                JSON.stringify(si.options) === JSON.stringify(local.options || {}) &&
                JSON.stringify(si.configuratorOptions) === JSON.stringify(local.configuratorOptions ?? null) &&
                (si.personalizationText || '') === (local.personalizationText || '')
              );
              if (serverMatch) {
                serverMatch.quantity += local.quantity;
                serverMatch.itemTotal = serverMatch.price * serverMatch.quantity;
              } else {
                merged.push({ ...local, cartItemId: local.cartItemId || generateCartItemId() });
              }
            }
            dispatch({ type: LOAD_CART, payload: merged });
            await syncToServer(merged, userId);
          }
        } catch (err) {
          logger.error('Error merging cart on login', { error: err.message });
        }
      } else if (event === 'SIGNED_OUT') {
        if (syncTimer.current) clearTimeout(syncTimer.current);
        // Keep local cart intact for guest browsing
      }
    });

    return () => {
      subscription.unsubscribe();
      if (syncTimer.current) clearTimeout(syncTimer.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addToCart = (product, quantity = 1, options = {}, personalizationText = '', configuratorOptions = null) => {
    if (!product) { logger.error('Attempted to add invalid product to cart', { product }); return; }
    const validatedProduct = {
      ...product,
      name: product.name || 'Product',
      price: typeof product.price === 'number' && !isNaN(product.price)
        ? product.price : (parseFloat(product.price) || 0),
      cartItemId: generateCartItemId(),
    };
    dispatch({
      type: ADD_TO_CART,
      payload: {
        product: validatedProduct,
        quantity: parseInt(quantity) || 1,
        options,
        personalizationText: typeof personalizationText === 'string' ? personalizationText.trim() : '',
        configuratorOptions,
      },
    });
  };

  const removeFromCart = (cartItemId) => dispatch({ type: REMOVE_FROM_CART, payload: cartItemId });

  const updateQuantity = (cartItemId, quantity) =>
    dispatch({ type: UPDATE_QUANTITY, payload: { cartItemId, quantity } });

  const clearCart = () => {
    dispatch({ type: CLEAR_CART });
    const userId = userRef.current;
    if (userId) supabase.from('cart_items').delete().eq('user_id', userId).then(() => {});
  };

  const getItemSubtotal = (item) => item.price * item.quantity;

  return (
    <CartContext.Provider value={{ cart: state, addToCart, removeFromCart, updateQuantity, clearCart, getItemSubtotal }}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
